import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { defaultWebDateRange, type WebDateRange } from './dataSourceSelections';

function toDatetimeLocalValue(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export type WebDateRangePanelProps = {
  value: WebDateRange | null;
  onChange: (next: WebDateRange | null) => void;
};

/**
 * Start/end controls for the Web data source. Empty both sides means
 * unrestricted search; Reset restores the last-five-days default.
 */
const WebDateRangePanel: React.FC<WebDateRangePanelProps> = ({
  value,
  onChange,
}) => {
  const [draftStart, setDraftStart] = useState(() =>
    toDatetimeLocalValue(value?.start)
  );
  const [draftEnd, setDraftEnd] = useState(() =>
    toDatetimeLocalValue(value?.end)
  );
  const [syncedStart, setSyncedStart] = useState(value?.start);
  const [syncedEnd, setSyncedEnd] = useState(value?.end);

  if (value?.start !== syncedStart || value?.end !== syncedEnd) {
    setSyncedStart(value?.start);
    setSyncedEnd(value?.end);
    setDraftStart(toDatetimeLocalValue(value?.start));
    setDraftEnd(toDatetimeLocalValue(value?.end));
  }

  const apply = (startLocal: string, endLocal: string) => {
    const startIso = fromDatetimeLocalValue(startLocal);
    const endIso = fromDatetimeLocalValue(endLocal);
    if (!startIso && !endIso) {
      onChange(null);
      return;
    }
    if (!startIso || !endIso) {
      toast.error('Set both a start and end date, or clear both.');
      return;
    }
    if (new Date(startIso).getTime() > new Date(endIso).getTime()) {
      toast.error('Start must be on or before end.');
      return;
    }
    onChange({ start: startIso, end: endIso });
  };

  return (
    <div className="dark tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground">
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Limit Web search results to a date range. Default is the last 5 days.
      </p>
      <div className="tw:grid tw:gap-2">
        <Label htmlFor="web-date-start">Start</Label>
        <Input
          id="web-date-start"
          type="datetime-local"
          value={draftStart}
          onChange={(event) => {
            const next = event.target.value;
            setDraftStart(next);
            apply(next, draftEnd);
          }}
        />
      </div>
      <div className="tw:grid tw:gap-2">
        <Label htmlFor="web-date-end">End</Label>
        <Input
          id="web-date-end"
          type="datetime-local"
          value={draftEnd}
          onChange={(event) => {
            const next = event.target.value;
            setDraftEnd(next);
            apply(draftStart, next);
          }}
        />
      </div>
      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setDraftStart('');
            setDraftEnd('');
            onChange(null);
          }}
        >
          Clear
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            const next = defaultWebDateRange();
            setDraftStart(toDatetimeLocalValue(next.start));
            setDraftEnd(toDatetimeLocalValue(next.end));
            onChange(next);
          }}
        >
          Reset to last 5 days
        </Button>
      </div>
    </div>
  );
};

export default WebDateRangePanel;
